import { CAMPING_YEAR } from "@/constants";
import {
  Check,
  ChevronDown,
  Circle,
  X,
  Tent,
  Shirt,
  ShieldCheck,
  Flashlight,
  GlassWater,
  Stethoscope,
  FileText,
  Ban,
  type LucideIcon,
} from "lucide-react";

interface PackItem {
  label: string;
  essential: boolean;
}

interface PackCategory {
  icon: LucideIcon;
  title: string;
  items: PackItem[];
}

const categories: PackCategory[] = [
  {
    icon: Tent,
    title: "Alojamiento",
    items: [
      { label: "Carpa (con mosquitero)", essential: true },
      { label: "Colchoneta o sleeping bag", essential: true },
      { label: "Candado", essential: false },
    ],
  },
  {
    icon: Shirt,
    title: "Ropa",
    items: [
      { label: "Ropa ligera y transpirable (2 a 3 mudas)", essential: true },
      { label: "Traje de baño (x2)", essential: true },
      { label: "Calzado cerrado (mejor si es de senderismo)", essential: true },
      { label: "Sandalias o chancletas", essential: true },
      { label: "Poncho o chubasquero ligero", essential: false },
      { label: "Sombrero o gorra", essential: true },
    ],
  },
  {
    icon: ShieldCheck,
    title: "Protección",
    items: [
      { label: "Repelente de insectos con DEET", essential: true },
      { label: "Protector solar FPS 50+", essential: true },
      { label: "Toalla de microfibra", essential: true },
      { label: "Anteojos de sol", essential: false },
    ],
  },
  {
    icon: Flashlight,
    title: "Iluminación y energía",
    items: [
      { label: "Linterna frontal (headlamp)", essential: true },
      { label: "Power bank / batería portátil", essential: true },
      { label: "Cable de carga", essential: true },
    ],
  },
  {
    icon: GlassWater,
    title: "Alimentación e hidratación",
    items: [
      { label: "Agua potable (mínimo 2L por día)", essential: true },
      { label: "Snacks energéticos (nueces, barras)", essential: true },
      { label: "Botella reutilizable", essential: true },
      { label: "Pastillas potabilizadoras", essential: false },
    ],
  },
  {
    icon: Stethoscope,
    title: "Botiquín básico",
    items: [
      { label: "Antihistamínico (picaduras/alergia)", essential: true },
      { label: "Analgésico / paracetamol", essential: true },
      { label: "Tiritas y gasa", essential: false },
      { label: "Sales de rehidratación", essential: false },
    ],
  },
  {
    icon: FileText,
    title: "Documentos y extras",
    items: [
      { label: "Cédula o pasaporte", essential: true },
      { label: "Reserva del camping (impresa o digital)", essential: true },
      { label: "Efectivo en COP (no hay cajeros dentro)", essential: true },
      { label: "Bolsa impermeable para documentos", essential: false },
      { label: "Mochila pequeña de día (10 a 15 L)", essential: true },
    ],
  },
  {
    icon: Ban,
    title: "No llevar",
    items: [
      { label: "Maleta grande: cargarás todo tú mismo", essential: false },
      { label: "Comida en envases de vidrio", essential: false },
      { label: "Sustancias alucinógenas", essential: false },
      { label: "Drones", essential: false },
    ],
  },
];

const handleDownload = async () => {
  const rows = categories
    .map(
      (cat) => `
      <div class="category">
        <h3>${cat.title}</h3>
        <ul>
          ${cat.items
            .map(
              (item) => `
            <li>
              <span class="checkbox">☐</span>
              ${item.label}${item.essential ? ' <span class="badge">esencial</span>' : ""}
            </li>`
            )
            .join("")}
        </ul>
      </div>`
    )
    .join("");

  const htmlContent = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Lista de Equipaje: Camping Parque Tayrona</title>
  <link href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Lato, ui-sans-serif, system-ui, -apple-system, sans-serif; color: #1a2e1a; background: #fff; padding: 40px; }
    header { text-align: center; margin-bottom: 32px; border-bottom: 2px solid #1e5c38; padding-bottom: 16px; }
    header h1 { font-size: 22px; color: #1e5c38; margin-bottom: 4px; }
    header p { font-size: 12px; color: #666; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
    .category { break-inside: avoid; }
    .category h3 { font-size: 13px; font-weight: bold; color: #1e5c38; border-bottom: 1px solid #c8e6c9; padding-bottom: 4px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em; }
    ul { list-style: none; }
    li { font-size: 12px; display: flex; align-items: baseline; gap: 6px; padding: 3px 0; border-bottom: 1px dotted #e8e8e8; }
    .checkbox { font-size: 14px; flex-shrink: 0; }
    .badge { font-size: 9px; background: #e8f5e9; color: #2e7d32; border-radius: 3px; padding: 1px 4px; font-weight: bold; text-transform: uppercase; flex-shrink: 0; }
    footer { text-align: center; margin-top: 32px; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 12px; }
  </style>
</head>
<body>
  <header>
    <h1>Lista de Equipaje: Parque Tayrona</h1>
    <p>campingparquetayrona.com · Actualizado ${CAMPING_YEAR} · Imprime y lleva contigo</p>
  </header>
  <div class="grid">${rows}</div>
  <footer>campingparquetayrona.com · Guía oficial de campings en el Parque Nacional Natural Tayrona, Colombia</footer>
</body>
</html>`;

  const element = document.createElement("div");
  element.innerHTML = htmlContent;

  const options = {
    margin: 10,
    filename: "lista-equipaje-tayrona.pdf",
    image: { type: "jpeg" as const, quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { orientation: "portrait" as const, unit: "mm", format: "a4" },
  };

  // Loaded on click, not with the page: html2pdf.js (jsPDF + html2canvas) is
  // most of the JS bundle and only this button needs it.
  const { default: html2pdf } = await import("html2pdf.js");
  html2pdf().set(options).from(element).save();
};

/**
 * Packing list (DESIGN.md). Each category is a native <details>: keyboard and
 * screen-reader support for free, all items in the HTML for crawlers.
 * Every category starts closed, at every screen size.
 */

const PackingList = () => {
  return (
    <section id="lista-equipaje" aria-labelledby="packing-heading" className="pq-section">
      <div className="pq-wrap" style={{ display: "grid", gap: 40 }}>
        <div className="pq-split">
          <div style={{ display: "grid", gap: 16 }}>
            <h2 id="packing-heading" className="pq-headline">
              ¿Qué llevar al camping en el Tayrona?
            </h2>
            <p className="pq-lead">
              Guía de equipaje para acampar en el Parque Tayrona. Descárgala en PDF e
              imprímela antes de salir. Ve ligero: cada kilo cuenta en la caminata
              hacia Cabo San Juan.
            </p>
          </div>
          <div className="pq-split-end">
            <button type="button" className="pq-btn pq-btn-primary" onClick={handleDownload}>
              Descargar lista en PDF
            </button>
            <p className="pq-meta pq-legend">
              <Check size={16} aria-hidden="true" /> Esencial
              <Circle size={14} aria-hidden="true" /> Opcional
            </p>
          </div>
        </div>

        <div className="pq-packs">
          {categories.map((cat) => {
            const noLlevar = cat.title === "No llevar";
            const esenciales = cat.items.filter((i) => i.essential).length;
            const resumen = noLlevar
              ? `${cat.items.length} cosas`
              : `${cat.items.length} cosas, ${esenciales} esenciales`;
            return (
              <details key={cat.title} className="pq-card pq-pack">
                <summary>
                  <cat.icon size={20} strokeWidth={1.75} aria-hidden="true" />
                  <span className="pq-pack-title">{cat.title}</span>
                  <span className="pq-meta pq-pack-count">{resumen}</span>
                  <ChevronDown size={18} aria-hidden="true" className="pq-pack-chevron" />
                </summary>
                <ul className="pq-pack-list">
                  {cat.items.map((item) => {
                    const Icon = noLlevar ? X : item.essential ? Check : Circle;
                    return (
                      <li key={item.label} className={item.essential || noLlevar ? "" : "pq-pack-optional"}>
                        <Icon size={noLlevar || item.essential ? 16 : 14} aria-hidden="true" />
                        <span>
                          {item.label}
                          {!noLlevar ? (
                            <span className="pq-sr">{item.essential ? " (esencial)" : " (opcional)"}</span>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PackingList;
